import React, { useEffect } from 'react';
import cx from 'classnames';
import ButtonV3 from '../ButtonV3/ButtonV3';
import { ErrorBoundary } from '../ErrorBoundary';
import { useCartContext } from './CartContext';
import { SecuritySeal } from './components/SecuritySeal';
import { compactCurrencyFilter } from './utils/PricingUtils';
import { tokenizePaypalPayment } from './PaypalCheckout';
import { DGCsUpsell } from './DGCsUpsell';
import { PaypalButtonWrapper } from './PaypalButtonWrapper';
import { trackCartViewed } from './utils/trackingHelpers';
import { Title1 } from '../../typography/Headings';
import { ShippingBanner } from './components/ShippingBanner';
import { EmptyCart } from './components/EmptyCart';

import styles from './cart.module.less';
import cartContainerStyles from './stylesheets/cartContainer.module.less';

import '../../paper/cards/stylesheets/_fonts.less';
import '../../paper/cards/stylesheets/_fonts2.less';
import { CartWrapper, CartContainer } from './components/CartContainer';
import { CartGrid } from './components/CartGrid';

/**
 * Cart with buttons to checkout, for use on the stand alone cart page or in the cart modal
 *
 * @returns
 */
export const Cart = () => {
  const {
    loaded,
    items,
    totals,
    isFreeShippingEligible,
    freeShipping,
    cartId,
    registryId,
    fetchCart,
    isBaby,
    isHomeStore,
    removeCartItem,
    onCheckout,
  } = useCartContext();
  const isEmpty = items.length === 0;
  const showShippingBanner = (isEmpty || isFreeShippingEligible || freeShipping) && !isBaby;
  const isDgcV5 = false; // keep for future test

  // Send cart viewed event for carts associated with registry purchases only
  useEffect(() => {
    if (registryId && cartId) {
      trackCartViewed(registryId, cartId, items);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartId, registryId]);

  return (
    <CartContainer
      className={cx(styles.zolaRegistryStyles, {
        [styles.zolaBabyStyles]: isBaby,
      })}
    >
      <div className={styles.cartHeader}>
        <Title1 strong presentation="h3">
          Your cart
        </Title1>
        {loaded && !isEmpty && (
          <ButtonV3
            variant="primary"
            onClick={onCheckout}
            className={cx(styles.checkoutButton, styles.checkoutTop)}
          >
            Check out now
          </ButtonV3>
        )}
      </div>
      {loaded ? (
        <CartWrapper>
          {showShippingBanner && <ShippingBanner />}
          {isEmpty && <EmptyCart />}
          {!isEmpty && (
            <CartGrid isDgcV5={isDgcV5}>
              <div className={styles.cartTotals}>
                <ErrorBoundary>
                  <DGCsUpsell
                    items={items}
                    updateCart={fetchCart}
                    isDgcV5={isDgcV5}
                    remove={removeCartItem}
                  />
                </ErrorBoundary>
                <div className={styles.cartSubTotalsWrapper}>
                  <SecuritySeal className={cartContainerStyles.hiddenContainerSmall} />
                  <div>
                    <div className={styles.cartSubTotalLabel}>Subtotal:</div>{' '}
                    <strong className={styles.cartSubTotal}>
                      {compactCurrencyFilter(totals?.subtotal || 0)}
                    </strong>
                    <br />
                    {(totals?.amountSaved || 0) > 0 && (
                      <small className={styles.cartTotalSavings}>
                        You&apos;re saving{' '}
                        <strong>{compactCurrencyFilter(totals?.amountSaved || 0)}</strong>
                      </small>
                    )}
                  </div>
                </div>
              </div>
            </CartGrid>
          )}
          <div className={styles.cartFooter}>
            {!isEmpty && (
              <div className={styles.checkoutButtonContainer}>
                <ButtonV3
                  id="cart-checkout-button"
                  className={cx(styles.checkoutButton, styles.checkoutButton)}
                  onClick={onCheckout}
                  variant="primary"
                >
                  Check out now
                </ButtonV3>
                <div className={styles.zolaPaypalButtonWrapper}>
                  <PaypalButtonWrapper
                    items={items}
                    amountCents={(totals?.subtotal || 0) * 100}
                    onApprove={tokenizePaypalPayment}
                    tokenUrl={
                      isHomeStore
                        ? '/web-store-api/v1/checkout/braintree/token'
                        : '/web-registry-api/v1/checkout/braintree/token'
                    }
                    collectionUrl={
                      isHomeStore
                        ? '/web-store-api/v1/registryCollection/item'
                        : '/web-registry-api/v1/registryCollection/item'
                    }
                  />
                </div>
                <div className={cx(cartContainerStyles.visibleContainerSmall, styles.securitySeal)}>
                  <SecuritySeal />
                </div>
              </div>
            )}
          </div>
        </CartWrapper>
      ) : (
        <div>Loading</div>
      )}
    </CartContainer>
  );
};

export default Cart;
